/**
 * TOSS Web Application - Navigation Row Component
 *
 * Clickable row component for navigation-style form fields.
 * Shows a label/placeholder and chevron arrow, used for opening modals/sheets.
 *
 * Features:
 * - Clickable row with chevron indicator
 * - Shows selected value or placeholder
 * - Disabled state support
 * - Error state styling
 * - Gray background variant
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

export interface NavigationRowProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Text to display (selected value or placeholder) */
  text: string;
  /** Whether showing placeholder text (uses lighter color) */
  isPlaceholder?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Error state */
  error?: boolean;
  /** Additional class names */
  className?: string;
  /** Left icon */
  leftIcon?: ReactNode;
  /** Use gray background style */
  grayBackground?: boolean;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Navigation row component for form fields that open modals/sheets
 *
 * @example
 * ```tsx
 * // As placeholder
 * <NavigationRow
 *   text="Выберите клиента"
 *   isPlaceholder
 *   onClick={() => setShowModal(true)}
 * />
 *
 * // With selected value
 * <NavigationRow
 *   text={selectedCustomer.name}
 *   onClick={() => setShowModal(true)}
 * />
 *
 * // Disabled state
 * <NavigationRow
 *   text="Сначала выберите клиента"
 *   isPlaceholder
 *   disabled
 * />
 * ```
 */
export const NavigationRow = forwardRef<HTMLButtonElement, NavigationRowProps>(
  (
    {
      text,
      isPlaceholder = false,
      disabled = false,
      error = false,
      className = '',
      leftIcon,
      grayBackground = false,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const baseClasses = [
      'w-full flex items-center justify-between',
      'py-3.5 px-0',
      'text-left',
      'transition-colors duration-150',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
      'border-b border-[var(--color-border)]',
    ];

    const stateClasses = disabled
      ? 'cursor-not-allowed opacity-50'
      : 'cursor-pointer';

    const bgClasses = grayBackground
      ? 'bg-[var(--color-bg-secondary)] rounded-lg px-4 border-0'
      : '';

    const errorClasses = error ? 'border-[var(--color-error)]' : '';

    const textClasses = isPlaceholder || disabled
      ? 'text-[var(--color-text-disabled)]'
      : 'text-[var(--color-text-primary)]';

    const allClasses = [
      ...baseClasses,
      stateClasses,
      bgClasses,
      errorClasses,
      className,
    ]
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    return (
      <button
        ref={ref}
        type={type}
        className={allClasses}
        disabled={disabled}
        {...props}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {leftIcon && (
            <span className="flex-shrink-0 text-[var(--color-text-disabled)]">{leftIcon}</span>
          )}
          {/* Кегль задаётся явно: раньше строка его не объявляла и наследовала корневые 17.6 px —
            заметно крупнее полей формы, рядом с которыми она стоит. */}
        <span className={`truncate text-md ${textClasses}`}>{text}</span>
        </div>
        <ChevronRight className="w-5 h-5 text-[var(--color-text-disabled)]" aria-hidden="true" />
      </button>
    );
  }
);

NavigationRow.displayName = 'NavigationRow';

export default NavigationRow;
